import { useQuery } from '@tanstack/react-query';

import { isBackendRefusal } from '@/shared/lib/backend-errors';
import { isUnreadableResponseError } from '@/shared/lib/response-shape';
import { ScopeContextStorage } from '@/shared/lib/scope-storage';
import { getUserProfile } from '../api/user';

export const userProfileQueryKey = (sessionId: string | null) => ['user-profile', sessionId] as const;

const PROFILE_READ_RETRY_LIMIT = 2;

const retriesProfileRead = (failureCount: number, error: unknown): boolean =>
  !isBackendRefusal(error) && !isUnreadableResponseError(error) && failureCount < PROFILE_READ_RETRY_LIMIT;

export function useUserProfile() {
  const sessionId = ScopeContextStorage.getCurrentSessionId();

  const query = useQuery({
    queryKey: userProfileQueryKey(sessionId),
    queryFn: () => getUserProfile(sessionId!),
    enabled: !!sessionId,
    retry: retriesProfileRead,
  });

  return {
    sessionId,
    user: query.data?.user ?? null,
    setting: query.data?.setting ?? null,
    isPending: query.isPending,
    isError: query.isError,
    isFetching: query.isFetching,
    hasAnswered: !query.isPending || query.failureCount > 0,
    refetch: query.refetch,
  };
}
